import type { QueryConstraint } from "firebase/firestore";
import type { FirestoreKey } from "./types";

export type FirestoreQueryKey = readonly [
  scope: "firestore",
  path: string,
  constraints: string,
];

// The SDK's QueryConstraint classes carry their parameters in underscore
// fields (verified against firebase 12.x: where -> _field/_op/_value,
// orderBy -> _field/_direction, limit -> _limit/_limitType, cursors ->
// _docOrFields). Reading them structurally keeps the key legible in
// devtools; anything unrecognised falls back to JSON so distinct
// constraints can never collapse into the same key.
type ConstraintLike = {
  type?: string;
  _field?: { segments?: string[] };
  _op?: string;
  _value?: unknown;
  _direction?: string;
  _limit?: number;
  _limitType?: string;
  _docOrFields?: unknown[];
};

const describeConstraint = (constraint: ConstraintLike): string => {
  const field = constraint._field?.segments?.join(".");

  switch (constraint.type) {
    case "where":
      if (field && constraint._op) {
        return `where(${field} ${constraint._op} ${JSON.stringify(constraint._value)})`;
      }
      break;
    case "orderBy":
      if (field) {
        return `orderBy(${field} ${constraint._direction ?? "asc"})`;
      }
      break;
    case "limit":
    case "limitToLast":
      if (typeof constraint._limit === "number") {
        return constraint._limitType === "L"
          ? `limitToLast(${constraint._limit})`
          : `limit(${constraint._limit})`;
      }
      break;
    case "startAt":
    case "startAfter":
    case "endAt":
    case "endBefore":
      return `${constraint.type}(${JSON.stringify(constraint._docOrFields)})`;
    default:
      break;
  }

  return JSON.stringify(constraint);
};

const describeConstraints = (constraints: ConstraintLike[]): string =>
  constraints.map(describeConstraint).join(" | ");

export const makeQueryKey = (
  key: FirestoreKey,
  constraints: QueryConstraint[] | undefined,
  userId: string,
): FirestoreQueryKey | undefined => {
  if (typeof key !== "string") return undefined;

  if (!key) return undefined;

  // key has userId, but we have no session, quit early
  if (key.includes("{userId}") && !userId) return undefined;

  const path = key.replace("{userId}", userId);

  return [
    "firestore",
    path,
    constraints ? describeConstraints(constraints) : "",
  ];
};

// TanStack always needs a query key, even for disabled queries; "disabled"
// sits in the path slot so these can never collide with a real listener key
export const makeDisabledQueryKey = (key: FirestoreKey): FirestoreQueryKey => [
  "firestore",
  "disabled",
  typeof key === "string" ? key : "",
];
